/**
 * Premium-Support-Chat — Panel
 *
 * Eine Komponente für zwei Modi (responsive):
 *   - Desktop (sm+):  Mini-Panel 380×560 px, schwebt unten rechts
 *                     über der Bubble. Slide+Fade-In aus der Bubble-Position.
 *   - Mobile (<sm):   Bottom-Sheet, volle Breite, Höhe 85vh, Slide-Up
 *                     vom unteren Rand. Safe-Area-aware.
 *
 * Lifecycle:
 *   - Wird IMMER gemountet wenn der User Zugang hat (keine Lazy-Mounts),
 *     damit der Open/Close-Übergang flüssig animiert ist.
 *   - Sichtbarkeit über data-state="open"/"closed" + Tailwind-Klassen
 *     (visibility, transform, opacity).
 *   - Schließen: X-Button im Header oder Esc-Taste.
 *   - Hintergrund-Seite bleibt sichtbar und scrollbar (Lenis nicht stoppen,
 *     Spec §3.5 Lenis-kompatibel).
 *
 * Schließen-Verhalten:
 *   User klickt X → Panel schließt → User ist exakt wieder auf derselben
 *   Inhaltsseite, exakt am selben Scroll-Stand. Keine Routenwechsel.
 *
 * Phase-1-Inhalt: Concierge-Karte als Header + Empty-State-Begrüßung.
 * Konversations-Thread und Eingabefeld kommen in späteren Schritten.
 *
 * Schrift: ausschließlich IBM Plex Mono (Plattform-Standard, lokal geladen
 * via /fonts/fonts.css — keine externen Calls). Hierarchie über Größe +
 * Opazität + Letter-Spacing, nicht über Schrift-Wechsel.
 *
 * Premium-Details:
 *   - glass-surface auf gesamtem Panel
 *   - Subtle Glow am Rand: shadow-[0_8px_40px_rgba(0,0,0,0.4)]
 *   - rounded-3xl Desktop / rounded-t-3xl Mobile
 *   - z-45 (über Bubble z-40, unter Modal z-50)
 *   - Reduced-Motion respektiert
 */

import React, { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { useChatAccess } from '../../hooks/useChatAccess';
import { useChat } from '../../contexts/ChatContext';
import { useSupportAvailability } from '../../hooks/useSupportAvailability';
import { useChatConversation, type UIMessage } from '../../hooks/useChatConversation';
import { getImageUrl } from '../../utils/imageUtils';
import { ChatPanelBody } from './ChatPanelBody';
import { ChatInput } from './ChatInput';
import { PushOnboardingBanner } from './PushOnboardingBanner';
import { PushDiagnose } from './PushDiagnose';
import { AdminChatPanelBody } from '../admin/support/AdminChatPanelBody';

/**
 * PendingReply: kompakte Information über die Nachricht, auf die geantwortet wird.
 * Wird beim Tap/Swipe auf eine Bubble gesetzt, fließt durch ChatInput in die
 * Send-Action und wird beim Senden / Schließen der Vorschau wieder geleert.
 */
export interface PendingReply {
  messageId: string;
  senderType: UIMessage['senderType'];
  /** Vorschau-Text (max 80 Zeichen). Bei reinen Bild-Nachrichten leer. */
  contentPreview: string;
  hasAttachments: boolean;
}

const FOX_ICON = '/images/branding/rf-fux-yellow.svg';

/**
 * Mappt den Backend-Indikator auf eine Ring-Farb-Klasse für den Logo-Avatar.
 * Der Ring um das Logo IST der Status-Indikator — kein separater Punkt mehr
 * neben dem Antwortzeit-Text.
 *
 * Pulse nur bei "active_now" (sehr starkes Signal: Support tippt gerade etwas).
 */
function getAvatarRingClasses(
  indicator: 'green' | 'orange' | 'gray' | undefined,
  isActiveNow: boolean
): string {
  const color =
    indicator === 'green'
      ? 'ring-emerald-400/70'
      : indicator === 'orange'
        ? 'ring-amber-400/70'
        : indicator === 'gray'
          ? 'ring-gray-400/60'
          : 'ring-brand/40'; // Fallback während Status lädt
  const pulse = isActiveNow ? 'animate-pulse motion-reduce:animate-none' : '';
  return `${color} ${pulse}`;
}

export function ChatPanel(): React.ReactElement | null {
  const access = useChatAccess();
  const { isPanelOpen, closePanel } = useChat();
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  const isAdmin = access.planSlug === 'admin';

  // Verfügbarkeit nur fetchen, wenn der User Zugang hat (sonst sinnlos).
  const availability = useSupportAvailability({ enabled: access.hasAccess });
  const status = availability.data;
  const isActiveNow = status?.status === 'active_now';

  // User-Konversations-State (nur bei Nicht-Admin geladen).
  // Hook ist nur aktiv wenn Panel sichtbar ist UND User-Modus.
  const conversationState = useChatConversation({
    enabled: access.hasAccess && isPanelOpen && !isAdmin
  });

  // Pending-Reply: User wischt/tippt auf eine Bubble → Reply-Modus aktiv,
  // bis er entweder die Nachricht abschickt oder die Vorschau wegklickt.
  // Lokal gehalten (nicht im Context), weil pro Konversations-Modus separat.
  const [pendingReply, setPendingReply] = useState<PendingReply | null>(null);

  // Wenn das Panel geschlossen wird, Reply-Modus zurücksetzen — sonst hängt
  // er am User, wenn er später wieder öffnet.
  useEffect(() => {
    if (!isPanelOpen) setPendingReply(null);
  }, [isPanelOpen]);

  // Esc schließt das Panel
  useEffect(() => {
    if (!isPanelOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closePanel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isPanelOpen, closePanel]);

  // Beim Öffnen: Fokus auf Schließen-Button (a11y, Tab-Reihenfolge fängt sauber an).
  // Beim Schließen: Bubble bekommt Fokus zurück (handled vom Browser durch
  // aria-controls/aria-expanded auf der Bubble).
  useEffect(() => {
    if (isPanelOpen) {
      // Kleiner Delay, damit das Element sichtbar ist bevor wir fokussieren
      const t = setTimeout(() => closeButtonRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [isPanelOpen]);

  // Scroll-Lock immer wenn Panel offen — sowohl Mobile (Fullscreen-Overlay)
  // als auch Desktop (verhindert dass der Hintergrund mitscrollt, sobald der
  // Chat am oberen/unteren Ende ankommt). overscroll-contain alleine reicht
  // nicht, weil Lenis (Smooth-Scroll) Scroll-Events am Browser vorbei verarbeitet.
  // Vorheriger Wert wird gespeichert, falls eine andere Stelle (z.B. Cookie-Banner)
  // parallel lockt.
  useEffect(() => {
    if (!isPanelOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [isPanelOpen]);

  // Schicht 2: Render-Gate. Kein DOM für nicht-berechtigte User.
  // Auch das geschlossene Panel wird nicht gemountet, wenn kein Zugang.
  if (!access.hasAccess) {
    return null;
  }

  return (
    <div
      id="chat-panel"
      role="dialog"
      aria-modal="false"
      aria-label="Priority Support Chat"
      data-state={isPanelOpen ? 'open' : 'closed'}
      // Position-Werte ausschließlich über Tailwind-Klassen (mit "!" force) —
      // Inline-style würde die sm:-Override per Spezifitäts-Regel schlagen
      // (Inline-Style > Klassen-Selektor) und das Panel auch auf Tablet/Desktop
      // fullscreen anzeigen statt als Mini-Panel unten rechts anzudocken.
      // Mobile (<640px): rundum mit Safe-Area-Padding (Notch/Home-Indicator)
      // sm+ (≥640px, inkl. iPad-Querformat): Mini-Panel unten rechts
      className="
        fixed z-[45]
        font-mono
        text-gray-900 dark:text-white
        rounded-3xl sm:rounded-3xl
        glass-surface
        overflow-hidden
        top-[max(env(safe-area-inset-top),0.5rem)]
        right-[max(env(safe-area-inset-right),0.25rem)]
        bottom-[max(env(safe-area-inset-bottom),0.25rem)]
        left-[max(env(safe-area-inset-left),0.25rem)]
        sm:!top-auto sm:!left-auto
        sm:!right-6 sm:!bottom-24
        sm:w-[380px] sm:h-[720px] sm:max-h-[calc(100dvh-7rem)]
        transition-all duration-200 ease-out
        motion-reduce:transition-none
        data-[state=closed]:opacity-0
        data-[state=closed]:pointer-events-none
        data-[state=closed]:translate-y-4
        sm:data-[state=closed]:translate-y-2
      "
    >
      {/* Inhalt-Container mit Flex-Spalte: Header / Body / (Footer kommt später) */}
      <div className="flex flex-col h-full">
        {/* ============================================================
            Header — kompakt: Avatar + Premium Support + Status
            Minimal-Design: kein extra "RENDITEFUCHS"-Label, Antwortzeit
            direkt unter "Premium Support" für maximale Platz-Effizienz.
            ============================================================ */}
        <header className="flex items-center gap-3 pl-2 pr-4 py-3 border-b border-white/5 dark:border-white/5">
          {/* Avatar im glass-surface-soft Kreis. Der Ring um das Logo IST
              der Verfügbarkeits-Indikator (grün/orange/grau, pulsierend bei
              "gerade aktiv"). */}
          <div
            className={`
              glass-surface-soft rounded-full w-7 h-7 p-1.5 ring-2 shrink-0
              flex items-center justify-center
              transition-colors duration-300
              ${getAvatarRingClasses(status?.indicator, isActiveNow)}
            `}
            title={status?.detail ?? status?.label ?? undefined}
            aria-label={status?.label ? `Status: ${status.label}` : undefined}
          >
            <img
              src={getImageUrl(FOX_ICON)}
              alt=""
              aria-hidden="true"
              className="w-4 h-4 object-contain"
            />
          </div>

          {/* Titel + dynamische Verfügbarkeits-Anzeige (jetzt ohne separaten
              Status-Punkt — der Ring um den Avatar zeigt den Status). */}
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.2em] opacity-50 leading-tight">
              Priority Support
            </div>
            <div className="mt-0.5" title={status?.detail ?? undefined}>
              <span className="text-[11px] opacity-70 leading-tight truncate block">
                {status?.label ?? 'Verfügbarkeit lädt …'}
              </span>
            </div>
          </div>

          {/* Schließen */}
          <button
            ref={closeButtonRef}
            type="button"
            onClick={closePanel}
            aria-label="Priority Support schließen"
            className="
              shrink-0
              w-8 h-8 rounded-full
              flex items-center justify-center
              opacity-60 hover:opacity-100
              hover:bg-white/5 dark:hover:bg-white/5
              transition-all duration-200
              focus:outline-none focus:ring-2 focus:ring-brand/40
            "
          >
            <X className="w-4 h-4" strokeWidth={2} />
          </button>
        </header>

        {/* Body-Render je nach Modus:
            Admin → Master-Detail-Inbox (Liste + Thread + eigenes Eingabefeld im Thread)
            User  → einzelne Konversation + festes Eingabefeld am Sheet-Boden */}
        {isAdmin ? (
          <>
            <AdminChatPanelBody enabled={access.hasAccess && isPanelOpen} />
            {/* Diagnose-Bereich am unteren Rand auch für Admins —
                hilft beim Troubleshooting der eigenen Push-Konfiguration. */}
            <PushDiagnose enabled={access.hasAccess && isPanelOpen} />
          </>
        ) : (
          <>
            <ChatPanelBody
              enabled={access.hasAccess && isPanelOpen}
              conversation={conversationState}
              onReply={(m) => setPendingReply({
                messageId: m.id,
                senderType: m.senderType,
                contentPreview: m.content.trim().slice(0, 80),
                hasAttachments: m.attachments.length > 0
              })}
            />
            {/* Push-Onboarding-Banner: nur wenn User schon mindestens eine
                Nachricht gesendet hat (sonst zu früh) und Push noch nicht
                aktiv ist (Hook entscheidet intern). */}
            <PushOnboardingBanner
              enabled={
                access.hasAccess &&
                isPanelOpen &&
                !isAdmin &&
                conversationState.messages.some((m) => m.senderType === 'user')
              }
            />
            <ChatInput
              onSend={conversationState.send}
              readOnly={conversationState.conversation?.isReadOnly === true}
              pendingReply={pendingReply}
              onClearReply={() => setPendingReply(null)}
              {...(conversationState.conversation?.isReadOnly === true && {
                readOnlyHint: 'Ihr Abo ist beendet — Sie können den Verlauf einsehen, aber keine neuen Nachrichten senden.'
              })}
            />
            <PushDiagnose enabled={access.hasAccess && isPanelOpen} />
          </>
        )}
      </div>
    </div>
  );
}
